/**
 * ============================================================================
 * UTILS.TS — Funcoes Utilitarias Globais
 * ============================================================================
 *
 * Proposito: Fornecer funcoes auxiliares reutilizaveis em todo o projeto,
 *   incluindo merge de classes CSS, formatacao de datas e manipulacao de strings.
 *
 * Exportacoes principais:
 *   - cn()                — Merge inteligente de classes Tailwind (clsx + twMerge)
 *   - formatDate()        — Formata data para "dd/MM/yyyy" (PT-BR)
 *   - formatDateTime()    — Formata data/hora para "dd/MM/yyyy HH:mm" (PT-BR)
 *   - getInitials()       — Extrai iniciais de um nome (ate 2 caracteres)
 *   - hasCommercialRole() — Verifica se uma string de roles contem um papel especifico
 *
 * Padrao de uso:
 *   <div className={cn("base-class", isActive && "active-class")} />
 * ============================================================================
 */

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type React from "react";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format a date string to "dd/MM/yyyy" */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Extract initials (up to 2 chars) from a name string */
export function getInitials(name: string): string {
  return name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
}

/**
 * Deterministic CSS-variable-friendly hue (0-359) from a name string.
 * Used to color avatar fallbacks by company, so the Kanban scans with
 * variety instead of every fallback being primary-tinted. Stable: the
 * same name always produces the same hue.
 *
 * Avoids reds (0-30°) and yellows (40-70°) that clash with status
 * colors (success/warning/destructive) by remapping into the 90-340
 * range — still gives ~250° of distinct hues.
 */
export function avatarHueFromName(name: string | undefined | null): number {
  if (!name) return 220; // neutral blue-ish fallback
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  // Map into [90, 340] (250° span) by % 250 + 90.
  return (h % 250) + 90;
}

/**
 * Style object for an Avatar fallback that uses the hue derived from
 * the name. Returns subtle bg + matching text color via HSL — works
 * in both light and dark modes since we're using lightness deltas.
 */
export function avatarFallbackStyle(name: string | undefined | null): React.CSSProperties {
  const hue = avatarHueFromName(name);
  return {
    backgroundColor: `hsl(${hue} 60% 92%)`,
    color: `hsl(${hue} 50% 30%)`,
  };
}
